import {
  Avatar,
  Badge,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import React from 'react';
import { Conversation } from '../../../types/messaging-types';
import { DateUtils } from '../../../utils';

interface ConversationListProps {
  conversations: Conversation[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}

export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  selectedId,
  onSelect,
}) => {
  const formatLastMessageTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return DateUtils.toDisplayDate(dateString);
    }
  };

  if (conversations.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No conversations found
        </Typography>
      </Box>
    );
  }

  return (
    <List sx={{ p: 0 }}>
      {conversations.map(conversation => (
        <ListItem key={conversation.id} disablePadding>
          <ListItemButton
            selected={conversation.id === selectedId}
            onClick={() => onSelect(conversation.id)}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'action.selected',
                '&:hover': {
                  backgroundColor: 'action.selected',
                },
              },
            }}
          >
            <ListItemAvatar>
              <Avatar
                src={conversation.otherParticipantAvatar}
                alt={conversation.otherParticipantName}
                sx={{ width: 40, height: 40 }}
              >
                {conversation.otherParticipantName.charAt(0).toUpperCase()}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: conversation.unreadCount > 0 ? 'bold' : 'normal',
                      flex: 1,
                    }}
                  >
                    {conversation.otherParticipantName}
                  </Typography>
                  {conversation.unreadCount > 0 && (
                    <Badge badgeContent={conversation.unreadCount} color="primary" />
                  )}
                </Box>
              }
              secondary={
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontWeight: conversation.unreadCount > 0 ? 'medium' : 'normal',
                    }}
                  >
                    {conversation.lastMessagePreview || 'No messages yet'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatLastMessageTime(conversation.lastMessageAt)}
                  </Typography>
                </Box>
              }
            />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
};
